import type { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { hasCompletedOnboarding } from '../components/onboarding';
import { ProtectedLesson } from '../components/layout/ProtectedLesson';
import type { RouteDefinition } from './types';
import { coreRoutes } from './coreRoutes';
import { practiceRoutes } from './practiceRoutes';
import { readingRoutes } from './readingRoutes';
import { sessionRoutes } from './sessionRoutes';

// Wrapper to redirect to onboarding if not completed
function RequireOnboarding({ children }: { children: React.ReactNode }) {
  if (!hasCompletedOnboarding()) {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

// Wrapper to redirect away from onboarding if already completed
function OnboardingGuard({ children }: { children: React.ReactNode }) {
  if (hasCompletedOnboarding()) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/** All application routes combined */
export const allRoutes: RouteDefinition[] = [
  ...coreRoutes,
  ...practiceRoutes,
  ...readingRoutes,
  ...sessionRoutes,
];

/** Wrap a route element with its guard component */
export function renderRoute(route: RouteDefinition): ReactElement {
  switch (route.guard) {
    case 'lesson':
      return <ProtectedLesson>{route.element}</ProtectedLesson>;
    case 'onboarding-guard':
      return <OnboardingGuard>{route.element}</OnboardingGuard>;
    case 'none':
      return <>{route.element}</>;
    case 'onboarding':
    default:
      return <RequireOnboarding>{route.element}</RequireOnboarding>;
  }
}
